/**
 * PCM playback: AudioWorklet on HTTPS, queue on HTTP (LAN without secure context).
 */

/** Max queued samples (~5 s stereo @ 48 kHz). */
const MAX_QUEUE_SAMPLES = 480000;
/** Fallback schedules audio ahead to avoid gaps between 60 ms chunks. */
const FALLBACK_TARGET_AHEAD_SEC = 0.30;
/** Prevent fallback from drifting too far ahead of live on burst receive. */
const FALLBACK_MAX_AHEAD_SEC = 0.60;
/** Small lead for the first AudioBufferSource after start/flush. */
const FALLBACK_START_LEAD_SEC = 0.08;

/**
 * Creates PCM output for AudioContext.
 * @param {AudioContext} ctx
 */
export async function createPcmOutput(ctx, onUnderrun = () => {}, onReady = () => {}) {
  if (ctx.audioWorklet) {
    await ctx.audioWorklet.addModule(new URL("./pcm-processor.js", import.meta.url));
    const node = new AudioWorkletNode(ctx, "pcm-processor", {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      channelCount: 2,
      channelCountMode: "explicit",
      channelInterpretation: "speakers"
    });
    node.connect(ctx.destination);
    node.port.onmessage = (event) => {
      if (event.data?.type === "underrun") {
        onUnderrun(event.data.count);
      } else if (event.data?.type === "workletReady") {
        onReady(event.data.sampleRate);
      }
    };
    return {
      mode: "worklet",
      setPause(value) {
        node.port.postMessage({ type: "pause", value });
      },
      setMute(value) {
        node.port.postMessage({ type: "mute", value });
      },
      setGain(value) {
        node.port.postMessage({ type: "gain", value });
      },
      pushPcm(samples, channels = 1) {
        const copy = new Float32Array(samples);
        node.port.postMessage({ type: "pcm", samples: copy, channels }, [copy.buffer]);
      },
      flush() {
        node.port.postMessage({ type: "flush" });
      },
    };
  }

  return createBufferPlayer(ctx, onReady);
}

/**
 * Fallback for browsers without AudioWorklet.
 *
 * BufferSource is one-shot, so we schedule several short buffers ahead.
 * Do not wait for the previous 60-ms chunk onended: by then the next start
 * would be in the past and silence appears between frames.
 * @param {AudioContext} ctx
 * @param {(sampleRate: number) => void} [onReady]
 */
function createBufferPlayer(ctx, onReady = () => {}) {
  onReady(ctx.sampleRate);
  const gainNode = ctx.createGain();
  gainNode.connect(ctx.destination);

  let paused = false;
  let muted = false;
  let gain = 0.8;
  /** @type {Float32Array[]} */
  let queue = [];
  let queueSamples = 0;
  let scheduledUntil = 0;
  /** @type {Set<AudioBufferSourceNode>} */
  let active = new Set();
  let scheduling = false;

  gainNode.gain.value = gain;

  /** Schedules PCM up to target lead, keeping a continuous timeline. */
  function scheduleAhead() {
    if (scheduling || paused || muted || queue.length === 0) {
      return;
    }
    scheduling = true;
    try {
      const now = ctx.currentTime;
      // After start/flush or main-thread delay, start slightly ahead of now.
      // Otherwise AudioContext rejects start in the past.
      if (scheduledUntil < now + FALLBACK_START_LEAD_SEC) {
        scheduledUntil = now + FALLBACK_START_LEAD_SEC;
      }

      // Schedule about 300 ms. Upper bound guards against burst receive:
      // latency matters more than keeping old radio chunks.
      const targetUntil = now + FALLBACK_TARGET_AHEAD_SEC;
      const maxUntil = now + FALLBACK_MAX_AHEAD_SEC;
      while (queue.length > 0 && scheduledUntil < targetUntil && scheduledUntil < maxUntil) {
        const item = queue.shift();
        if (!item) {
          break;
        }
        const samples = item.samples;
        const ch = item.channels || 1;
        queueSamples -= samples.length;

        const frames = ch > 1 ? samples.length / ch : samples.length;
        const buffer = ctx.createBuffer(ch, frames, ctx.sampleRate);
        if (ch === 1) {
          buffer.copyToChannel(samples, 0);
        } else {
          const left = new Float32Array(frames);
          const right = new Float32Array(frames);
          for (let i = 0; i < frames; i += 1) {
            left[i] = samples[i * 2];
            right[i] = samples[i * 2 + 1];
          }
          buffer.copyToChannel(left, 0);
          buffer.copyToChannel(right, 1);
        }

        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(gainNode);
        active.add(source);
        source.start(scheduledUntil);
        scheduledUntil += buffer.duration;

        source.onended = () => {
          active.delete(source);
          scheduleAhead();
        };
      }
    } finally {
      scheduling = false;
    }
  }

  return {
    mode: "buffer",
    setPause(value) {
      paused = value;
      if (paused) {
        for (const source of active) {
          try {
            source.stop();
          } catch {
            /* already stopped */
          }
        }
        active.clear();
        scheduledUntil = 0;
      } else {
        scheduleAhead();
      }
    },
    setMute(value) {
      muted = value;
      gainNode.gain.value = muted ? 0 : gain;
    },
    setGain(value) {
      gain = value;
      if (!muted) {
        gainNode.gain.value = gain;
      }
    },
    pushPcm(samples, channels = 1) {
      if (paused || muted) {
        return;
      }

      const copy = new Float32Array(samples);
      queue.push({ samples: copy, channels });
      queueSamples += copy.length;

      while (queueSamples > MAX_QUEUE_SAMPLES && queue.length > 0) {
        const dropped = queue.shift();
        if (dropped) {
          queueSamples -= dropped.samples.length;
        }
      }

      scheduleAhead();
    },
    flush() {
      queue = [];
      queueSamples = 0;
      scheduledUntil = 0;
      for (const source of active) {
        try {
          source.stop();
        } catch {
          /* ok */
        }
      }
      active.clear();
    },
  };
}
